import { useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Camera, Loader2, X, MapPin, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { CameraCapture } from "@/components/CameraCapture";
import { Lightbox } from "@/components/Lightbox";
import { requestLocation, getPosition } from "@/lib/geo";

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

// Draw image to canvas and burn in date/time + GPS coordinates
async function stampImage(file, pos, capturedAt) {
  const img = await loadImage(file);
  const maxW = 1600;
  const scale = Math.min(1, maxW / img.width);
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);

  const pad = Math.round(w * 0.02);
  const fs = Math.max(14, Math.round(w * 0.022));
  const line1 = `RetroTrack  •  ${new Date(capturedAt).toLocaleString("en-GB")}`;
  const line2 = pos
    ? `GPS ${pos.lat.toFixed(6)}, ${pos.lng.toFixed(6)}  (±${Math.round(pos.accuracy)}m)`
    : "GPS location unavailable";
  const barH = fs * 2 + pad * 2.2;
  ctx.fillStyle = "rgba(15,23,42,0.72)";
  ctx.fillRect(0, h - barH, w, barH);
  ctx.fillStyle = "#4ADE80";
  ctx.fillRect(0, h - barH, Math.round(w * 0.012), barH);
  ctx.font = `bold ${fs}px sans-serif`;
  ctx.fillStyle = "#ffffff";
  ctx.textBaseline = "top";
  ctx.fillText(line1, pad * 1.5, h - barH + pad * 0.7);
  ctx.font = `${fs}px monospace`;
  ctx.fillStyle = "#E2E8F0";
  ctx.fillText(line2, pad * 1.5, h - barH + pad * 0.9 + fs);

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", 0.85));
}

export function PhotoField({ photos = [], onChange, testId, single = false }) {
  const galleryRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [camOpen, setCamOpen] = useState(false);
  const [streamPromise, setStreamPromise] = useState(null);
  const [locationPromise, setLocationPromise] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  // iOS Safari only prompts for the camera when getUserMedia is initiated inside the tap handler
  const openCamera = () => {
    let sp = null;
    if (navigator.mediaDevices?.getUserMedia) {
      sp = navigator.mediaDevices
        .getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false })
        .catch(() => navigator.mediaDevices.getUserMedia({ video: true, audio: false }));
      sp.catch(() => {});
    }
    setStreamPromise(sp);
    // iOS Safari only prompts for location when requested inside the tap handler too
    setLocationPromise(requestLocation());
    setCamOpen(true);
  };

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const pos = await getPosition();
      const added = [];
      for (const file of Array.from(files)) {
        const capturedAt = new Date().toISOString();
        const blob = await stampImage(file, pos, capturedAt);
        const fd = new FormData();
        fd.append("file", blob, `photo-${Date.now()}.jpg`);
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        added.push({
          id: data.id,
          lat: pos?.lat ?? null,
          lng: pos?.lng ?? null,
          accuracy: pos?.accuracy ?? null,
          captured_at: capturedAt,
        });
        if (single) break;
      }
      onChange(single ? added.slice(0, 1) : [...photos, ...added]);
      toast.success(pos ? `${added.length > 1 ? added.length + " photos" : "Photo"} captured & geotagged` : "Photo saved (no GPS)");
    } catch (e) {
      toast.error("Photo capture failed");
    } finally {
      setBusy(false);
      if (galleryRef.current) galleryRef.current.value = "";
    }
  };

  const remove = (id) => onChange(photos.filter((p) => p.id !== id));
  const setCaption = (id, caption) => onChange(photos.map((p) => (p.id === id ? { ...p, caption } : p)));

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {photos.map((p) => (
          <div key={p.id} className="group relative flex flex-col overflow-hidden rounded-md border border-slate-200">
            <div className="relative">
              <img src={fileUrl(p.id, 400)} alt="site" onClick={() => setLightbox(fileUrl(p.id))} className="aspect-[4/3] w-full cursor-pointer object-cover" data-testid={`photo-thumb-${p.id}`} />
              <button
                type="button"
                onClick={() => remove(p.id)}
                className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
                data-testid={`remove-photo-${p.id}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
              {p.lat != null && (
                <div className="absolute bottom-0 left-0 right-0 flex items-center gap-1 bg-slate-900/80 px-2 py-1 text-[10px] font-medium text-white">
                  <MapPin className="h-3 w-3 text-accent" />
                  {p.lat.toFixed(4)}, {p.lng.toFixed(4)}
                </div>
              )}
            </div>
            <input
              value={p.caption || ""}
              onChange={(e) => setCaption(p.id, e.target.value)}
              placeholder="Add a caption…"
              className="w-full border-0 border-t border-slate-100 px-2 py-1.5 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-accent"
              data-testid={`caption-${p.id}`}
            />
          </div>
        ))}
        {(!single || photos.length === 0) && (
          <div className="flex aspect-[4/3] min-h-[104px] flex-col gap-2">
            <button
              type="button"
              onClick={openCamera}
              disabled={busy}
              className={`flex flex-1 flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed border-slate-300 text-slate-400 transition-colors hover:border-accent hover:text-accent ${single ? "" : ""}`}
              data-testid={testId}
            >
              {busy ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : (
                <>
                  <Camera className="h-6 w-6" />
                  <span className="text-xs font-semibold">{single ? "Take photo" : "Camera"}</span>
                </>
              )}
            </button>
            {!single && (
              <button
                type="button"
                onClick={() => galleryRef.current?.click()}
                disabled={busy}
                className="flex items-center justify-center gap-1.5 rounded-md border border-slate-300 py-2 text-slate-500 transition-colors hover:border-accent hover:text-accent"
                data-testid={testId ? `${testId}-multi` : "add-multiple-photos"}
              >
                <ImagePlus className="h-4 w-4" />
                <span className="text-xs font-semibold">Add multiple</span>
              </button>
            )}
          </div>
        )}
      </div>
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
      <CameraCapture open={camOpen} onClose={() => setCamOpen(false)} photos={photos} onChange={onChange} single={single} streamPromise={streamPromise} locationPromise={locationPromise} />
      <Lightbox src={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}
